"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.SimpleAuthService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const jwt_1 = require("@nestjs/jwt");
const simple_auth_repository_1 = require("./simple-auth.repository");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const password_util_1 = require("../../../../service-lib/src/lib/utils/password.util");
const environment_1 = require("../../../../service-lib/src/lib/environment");
let SimpleAuthService = class SimpleAuthService {
    constructor(userRepository, jwtService) {
        this.userRepository = userRepository;
        this.jwtService = jwtService;
    }
    async validateUser(loginName, password) {
        try {
            const user = await this.userRepository.findByLoginNameOrEmail(loginName);
            if (user && (await (0, password_util_1.isPasswordMatch)(password, user.password ?? ""))) {
                const accessToken = await this.getTokens(user);
                delete user.password;
                return {
                    accessToken,
                };
            }
            throw new common_1.ForbiddenException(messages_1.errorMessages.invalidCredentials);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException) {
                throw error;
            }
            throw new common_1.NotFoundException(messages_1.errorMessages.userNotFound);
        }
    }
    /**
     * Generates access and refresh tokens for a user.
     * @param {User} userDetails - The user entity.
     * @returns {Promise<{ accessToken: string }>} Tokens object.
     */
    /**
     * Generates access and refresh tokens for a user.
     * @param {User} userDetails - The user entity.
     * @returns {Promise<{ accessToken: string; refreshToken: string }>} Tokens object.
     */
    async getTokens(userDetails) {
        try {
            // Validate user details
            if (!userDetails) {
                throw new common_1.ForbiddenException("User details are required to generate tokens.");
            }
            if (!userDetails.userId) {
                throw new common_1.ForbiddenException("User ID is missing in user details.");
            }
            // Prepare the payload for the tokens
            const payload = {
                userDetails: {
                    departmentId: userDetails.departmentId,
                    emailId: userDetails.emailId,
                    userId: userDetails.userId,
                    iirmId: userDetails.iirmEmpId,
                    roles: userDetails?.roles,
                },
            };
            // Generate access token
            const accessToken = await this.jwtService.signAsync(payload, {
                expiresIn: environment_1.ENV.JWT_EXPIRATION, // Access token expiration time
            });
            // Generate refresh token
            const refreshToken = await this.jwtService.signAsync(payload, {
                expiresIn: environment_1.ENV.JWT_REFRESH_EXPIRATION, // Refresh token expiration time
            });
            return {
                accessToken,
                refreshToken,
            };
        }
        catch (error) {
            console.error("Error generating tokens:", error);
            throw new common_1.ForbiddenException(error.message || "An error occurred while generating tokens.");
        }
    }
    /**
     * Validates the refresh token and generates a new access token.
     * @param {string} refreshToken - The refresh token to validate.
     * @returns {Promise<{ accessToken: string }>} - The new access token.
     */
    async validateRefreshTokenAndGenerateAccessToken(refreshToken) {
        try {
            // Validate input
            if (!refreshToken?.trim()) {
                throw new common_1.ForbiddenException("Refresh token is required.");
            }
            // Verify the refresh token
            const payload = await this.jwtService.verifyAsync(refreshToken);
            if (!payload || !payload.userDetails) {
                throw new common_1.ForbiddenException("Invalid refresh token.");
            }
            // Extract user details from the payload
            const userDetails = payload.userDetails;
            // Validate user details
            if (!userDetails.userId) {
                throw new common_1.ForbiddenException("Invalid user details in refresh token.");
            }
            // Generate a new access token
            const newAccessToken = await this.jwtService.signAsync({ userDetails }, { expiresIn: environment_1.ENV.JWT_EXPIRATION } // Access token expiration time
            );
            return { accessToken: newAccessToken };
        }
        catch (error) {
            console.log("Error validating refresh token:", error);
            if (error.name === "TokenExpiredError") {
                throw new common_1.ForbiddenException("Refresh token has expired.");
            }
            else if (error.name === "JsonWebTokenError") {
                throw new common_1.ForbiddenException("Invalid refresh token.");
            }
            throw new common_1.ForbiddenException(error.message || "An error occurred while validating the refresh token.");
        }
    }
    async getUserDetails(userId) {
        try {
            const userDetails = await this.userRepository.findById(userId);
            return userDetails;
        }
        catch (error) {
            throw error;
        }
    }
};
exports.SimpleAuthService = SimpleAuthService;
exports.SimpleAuthService = SimpleAuthService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof simple_auth_repository_1.UserRepository !== "undefined" && simple_auth_repository_1.UserRepository) === "function" ? _a : Object, typeof (_b = typeof jwt_1.JwtService !== "undefined" && jwt_1.JwtService) === "function" ? _b : Object])
], SimpleAuthService);
